import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Crown, Lock, BookOpen, Bookmark, BookmarkCheck, Download, Play } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import RecRow from "@/components/RecRow";
import Reviews from "@/components/Reviews";

export default function BookDetail() {
  const { slug } = useParams();
  const { user } = useApp();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [inLib, setInLib] = useState(false);

  const load = () => api.get(`/books/${slug}`).then((r) => { setB(r.data); setInLib(r.data.in_library); }).catch(() => setB(false));
  useEffect(() => { load(); }, [slug, user]); // eslint-disable-line

  if (b === false) return <div className="py-24 text-center text-muted-foreground">Book not found.</div>;
  if (!b) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  const startCh = b.progress?.current_chapter || 1;
  const toggleLib = async () => {
    if (!user) return nav("/login");
    const { data } = await api.post("/library/toggle", { book_id: b.id });
    setInLib(data.in_library);
    toast.success(data.in_library ? "Saved to your library" : "Removed from library");
  };
  const download = async () => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.get(`/books/${slug}/download`);
      const blob = new Blob([data.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = data.filename; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fade-in">
      <div className="relative">
        {b.cover && <div className="absolute inset-0 h-80 overflow-hidden"><img src={b.cover} alt="" className="w-full h-full object-cover blur-2xl opacity-30 scale-110" /></div>}
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-52 shrink-0 mx-auto md:mx-0">
              <div className="aspect-[2/3] rounded-2xl overflow-hidden border border-border shadow-2xl">
                {b.cover ? <img src={b.cover} alt={b.title} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center bg-secondary"><BookOpen className="h-10 w-10 text-muted-foreground" /></div>}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-3">
                {b.genres.map((g) => <Link key={g.id} to={`/genres/${g.slug}`} className="text-xs px-3 py-1 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-colors">{g.name}</Link>)}
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold">{b.title}</h1>
              {b.author && <Link to={`/authors/${b.author.slug}`} className="inline-block text-lg text-muted-foreground mt-2 hover:text-primary">by {b.author.name}</Link>}
              {b.series && <div className="mt-1"><Link to={`/series/${b.series.slug}`} className="text-sm text-primary">{b.series.name} · Book {b.book_number} of {b.series.total}</Link></div>}
              <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                {b.rating_count > 0
                  ? <a href="#reviews" className="flex items-center gap-1 text-amber-400" data-testid="book-avg-rating"><Star className="h-4 w-4 fill-current" />{Number(b.rating).toFixed(1)} <span className="text-muted-foreground">({b.rating_count})</span></a>
                  : <a href="#reviews" className="flex items-center gap-1 hover:text-primary" data-testid="book-avg-rating"><Star className="h-4 w-4" />No ratings yet</a>}
                <span>{b.chapters?.length || 0} chapters</span>
                <span>{(b.read_count || 0).toLocaleString()} reads</span>
                {b.premium && <span className="flex items-center gap-1 text-amber-400"><Crown className="h-4 w-4" />Premium</span>}
              </div>
              <p className="text-lg italic text-foreground/90 mt-5 font-display">"{b.hook}"</p>
              <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">{b.description}</p>

              <div className="flex flex-wrap gap-2 mt-5">
                {b.tropes.map((t) => <Link key={t.id} to={`/tropes/${t.slug}`} className="text-xs px-3 py-1 rounded-full border border-border hover:border-primary transition-colors">{t.name}</Link>)}
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-8">
                <Link to={`/read/${b.slug}/${startCh}`} data-testid="read-book-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90">
                  <Play className="h-4 w-4 fill-current" /> {b.progress ? "Continue Reading" : "Start Reading"}
                </Link>
                <button onClick={toggleLib} data-testid="library-toggle-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border hover:border-primary transition-colors">
                  {inLib ? <BookmarkCheck className="h-4 w-4 text-primary" /> : <Bookmark className="h-4 w-4" />} {inLib ? "In Library" : "Save"}
                </button>
                {b.downloadable && <button onClick={download} data-testid="download-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border hover:border-primary transition-colors"><Download className="h-4 w-4" /> Download</button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pb-16">
        <h2 className="text-2xl font-display font-bold mb-4">Chapters</h2>
        <div className="space-y-2">
          {b.chapters?.map((c) => (
            <Link key={c.number} to={`/read/${b.slug}/${c.number}`} data-testid={`chapter-row-${c.number}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-border surface-2 hover:border-primary/60 transition-colors">
              <span className="text-sm font-mono text-muted-foreground w-8">{c.number}</span>
              <span className="font-medium flex-1">{c.title}</span>
              <span className="text-xs text-muted-foreground hidden sm:block">{c.word_count} words</span>
              {c.locked && <span className="flex items-center gap-1 text-xs text-amber-400"><Lock className="h-3.5 w-3.5" />{c.reason === "premium" ? "Premium" : "Unlock"}</span>}
            </Link>
          ))}
        </div>
        <div className="mt-16"><Reviews slug={slug} onChange={load} /></div>
        <div className="mt-16"><RecRow endpoint={`/books/${slug}/similar?limit=10`} title="Similar Books You'll Love" testid="similar-books" /></div>
      </div>
    </div>
  );
}
